// Central player-preference store. All settings the player can tune
// from the Settings modal live here: typed, defaulted, persisted to
// localStorage, and broadcast to consumers via a single change event.
//
// Consumers call `getPrefs()` for the live snapshot or read named
// scalar helpers (haptic, motionMul, densityMul, bloomEnabled,
// pixelRatioCap). On `onPrefsChange` they re-apply whatever they
// derive — touch UI rebuilds, renderer re-binds pixel ratio, etc.

export type MotionTier = 'off' | 'subtle' | 'normal' | 'intense';
export type DensityTier = 'off' | 'sparse' | 'normal' | 'lush';
export type PerfPreset = 'auto' | 'low' | 'medium' | 'high';
export type BloomMode = 'auto' | 'on' | 'off';
export type PixelRatioMode = 'auto' | '1.0' | '1.5' | '2.0';
export type AnchorH = 'left' | 'right';
export type DamageNumbersMode = 'all' | 'mine' | 'off';

export type Prefs = {
  haptics: boolean;
  motionShake: MotionTier;
  motionHitstop: MotionTier;
  particleDensity: DensityTier;
  perfPreset: PerfPreset;
  perfBloom: BloomMode;
  perfPixelRatio: PixelRatioMode;
  showFps: boolean;
  showDebugOverlay: boolean;
  showControlsHelper: boolean;
  damageNumbers: DamageNumbersMode;
  touchJoystickSize: number;
  touchButtonSize: number;
  touchLookSens: number;
  touchJoystickAnchor: AnchorH;
  touchButtonsAnchor: AnchorH;
  touchJoystickOffsetX: number;
  touchJoystickOffsetY: number;
  touchButtonsOffsetX: number;
  touchButtonsOffsetY: number;
};

const KEY: Record<keyof Prefs, string> = {
  haptics: 'smp.haptics',
  motionShake: 'smp.motionShake',
  motionHitstop: 'smp.motionHitstop',
  particleDensity: 'smp.motionParticles',
  perfPreset: 'smp.perfPreset',
  perfBloom: 'smp.perfBloom',
  perfPixelRatio: 'smp.perfPixelRatio',
  showFps: 'smp.showFps',
  showDebugOverlay: 'smp.showDebugOverlay',
  showControlsHelper: 'smp.showControlsHelper',
  damageNumbers: 'smp.damageNumbers',
  touchJoystickSize: 'smp.touchJoystickSize',
  touchButtonSize: 'smp.touchButtonSize',
  touchLookSens: 'smp.touchLookSens',
  touchJoystickAnchor: 'smp.touchJoystickAnchor',
  touchButtonsAnchor: 'smp.touchButtonsAnchor',
  touchJoystickOffsetX: 'smp.touchJoystickOffX',
  touchJoystickOffsetY: 'smp.touchJoystickOffY',
  touchButtonsOffsetX: 'smp.touchButtonsOffX',
  touchButtonsOffsetY: 'smp.touchButtonsOffY',
};

const DEFAULTS: Prefs = {
  haptics: true,
  motionShake: 'normal',
  motionHitstop: 'normal',
  particleDensity: 'normal',
  perfPreset: 'auto',
  perfBloom: 'auto',
  perfPixelRatio: 'auto',
  showFps: false,
  showDebugOverlay: false,
  showControlsHelper: false,
  damageNumbers: 'all',
  touchJoystickSize: 130,
  touchButtonSize: 78,
  touchLookSens: 1.0,
  touchJoystickAnchor: 'left',
  touchButtonsAnchor: 'right',
  touchJoystickOffsetX: 24,
  touchJoystickOffsetY: 24,
  touchButtonsOffsetX: 24,
  touchButtonsOffsetY: 24,
};

function readRaw(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeRaw(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore quota */ }
}

function loadInitial(): Prefs {
  const out: Prefs = { ...DEFAULTS };
  for (const key of Object.keys(KEY) as Array<keyof Prefs>) {
    const raw = readRaw(KEY[key]);
    if (raw == null) continue;
    const def = DEFAULTS[key];
    if (typeof def === 'boolean') {
      (out as Record<string, unknown>)[key] = raw === '1' || raw === 'true';
    } else if (typeof def === 'number') {
      const n = Number(raw);
      if (Number.isFinite(n)) (out as Record<string, unknown>)[key] = n;
    } else {
      // string-union — accept any persisted value; UI restricts inputs.
      (out as Record<string, unknown>)[key] = raw;
    }
  }
  return out;
}

const prefs: Prefs = loadInitial();

type Handler = (changed: Set<keyof Prefs>) => void;
const handlers = new Set<Handler>();

function emit(changed: Set<keyof Prefs>): void {
  for (const h of handlers) {
    try { h(changed); } catch (err) { console.warn('[prefs] handler threw', err); }
  }
}

export function getPrefs(): Readonly<Prefs> { return prefs; }

export function setPref<K extends keyof Prefs>(k: K, v: Prefs[K]): void {
  prefs[k] = v;
  const raw = typeof v === 'boolean' ? (v ? '1' : '0') : String(v);
  writeRaw(KEY[k], raw);
  emit(new Set<keyof Prefs>([k]));
}

export function setPrefs(patch: Partial<Prefs>): void {
  const changed = new Set<keyof Prefs>();
  for (const key of Object.keys(patch) as Array<keyof Prefs>) {
    const v = patch[key];
    if (v === undefined) continue;
    (prefs as Record<string, unknown>)[key] = v;
    const raw = typeof v === 'boolean' ? (v ? '1' : '0') : String(v);
    writeRaw(KEY[key], raw);
    changed.add(key);
  }
  if (changed.size > 0) emit(changed);
}

export function onPrefsChange(handler: Handler): () => void {
  handlers.add(handler);
  return () => { handlers.delete(handler); };
}

export function resetTouchLayout(): void {
  setPrefs({
    touchJoystickAnchor: DEFAULTS.touchJoystickAnchor,
    touchButtonsAnchor: DEFAULTS.touchButtonsAnchor,
    touchJoystickOffsetX: DEFAULTS.touchJoystickOffsetX,
    touchJoystickOffsetY: DEFAULTS.touchJoystickOffsetY,
    touchButtonsOffsetX: DEFAULTS.touchButtonsOffsetX,
    touchButtonsOffsetY: DEFAULTS.touchButtonsOffsetY,
  });
}

// Section-scoped resets so per-section "Restore Defaults" buttons in
// the settings UI revert just that section without nuking unrelated
// preferences.
const CONTROLS_KEYS: Array<keyof Prefs> = [
  'haptics',
  'touchJoystickSize',
  'touchButtonSize',
  'touchLookSens',
  'touchJoystickAnchor',
  'touchButtonsAnchor',
  'touchJoystickOffsetX',
  'touchJoystickOffsetY',
  'touchButtonsOffsetX',
  'touchButtonsOffsetY',
];
const PERFORMANCE_KEYS: Array<keyof Prefs> = [
  'perfPreset',
  'perfBloom',
  'perfPixelRatio',
  'showFps',
  'showDebugOverlay',
  'showControlsHelper',
];
const ACCESSIBILITY_KEYS: Array<keyof Prefs> = [
  'motionShake',
  'motionHitstop',
  'particleDensity',
  'damageNumbers',
];

export function resetSection(section: 'controls' | 'performance' | 'accessibility'): void {
  const keys =
    section === 'controls'      ? CONTROLS_KEYS :
    section === 'performance'   ? PERFORMANCE_KEYS :
                                  ACCESSIBILITY_KEYS;
  const patch: Partial<Prefs> = {};
  for (const k of keys) {
    (patch as Record<string, unknown>)[k] = DEFAULTS[k];
  }
  setPrefs(patch);
}

// ---- Side-effect helpers ----

// Gated wrapper around navigator.vibrate. Touch buttons + level-up +
// kill events all funnel through this so a single haptics off-switch
// silences every vibration in the game.
export function haptic(pattern: number | number[]): void {
  if (!prefs.haptics) return;
  try { navigator.vibrate?.(pattern); } catch { /* unsupported / blocked */ }
}

export function motionMul(tier: MotionTier): number {
  switch (tier) {
    case 'off':     return 0;
    case 'subtle':  return 0.4;
    case 'normal':  return 1.0;
    case 'intense': return 1.5;
  }
}

export function densityMul(tier: DensityTier): number {
  switch (tier) {
    case 'off':    return 0;
    case 'sparse': return 0.5;
    case 'normal': return 1.0;
    case 'lush':   return 1.5;
  }
}

// Auto-detection mirrors the original lowSpec heuristic from
// src/scene/postprocess.ts so 'auto' preset behaviour is unchanged
// when the player hasn't picked a manual quality tier.
function autoLowSpec(): boolean {
  if (typeof window === 'undefined') return false;
  return devicePixelRatio < 1.5 && innerWidth < 900;
}

export function bloomEnabled(): boolean {
  if (prefs.perfBloom === 'on') return true;
  if (prefs.perfBloom === 'off') return false;
  if (prefs.perfPreset === 'low' || prefs.perfPreset === 'medium') return false;
  if (prefs.perfPreset === 'high') return true;
  return !autoLowSpec();
}

export function pixelRatioCap(): number {
  if (prefs.perfPixelRatio === '1.0') return 1.0;
  if (prefs.perfPixelRatio === '1.5') return 1.5;
  if (prefs.perfPixelRatio === '2.0') return 2.0;
  if (prefs.perfPreset === 'low') return 1.0;
  if (prefs.perfPreset === 'medium') return 1.5;
  if (prefs.perfPreset === 'high') return 2.0;
  // auto — preserve the legacy Math.min(devicePixelRatio, 2) cap.
  return typeof window === 'undefined' ? 1 : Math.min(devicePixelRatio, 2);
}

// Apply prefers-reduced-motion as the first-boot default for any motion
// tier the player hasn't explicitly set. Run AFTER initial cache load
// so we know whether each key was previously stored. setPrefs persists
// the new values so they show up as explicit choices in the settings UI.
function applyReducedMotionDefault(): void {
  if (typeof matchMedia === 'undefined') return;
  let reduced: boolean;
  try { reduced = matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return; }
  if (!reduced) return;
  const patch: Partial<Prefs> = {};
  if (readRaw(KEY.motionShake) == null) patch.motionShake = 'subtle';
  if (readRaw(KEY.motionHitstop) == null) patch.motionHitstop = 'subtle';
  if (readRaw(KEY.particleDensity) == null) patch.particleDensity = 'sparse';
  if (Object.keys(patch).length > 0) setPrefs(patch);
}
applyReducedMotionDefault();
